import React, { useState } from "react";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, Link, Tooltip } from "@mui/material";
import { Edit, Delete, OpenInNew, Warning } from "components/icons";
import { parseLocalDate, startOfLocalDay } from "utils/dateUtil";

const getStatusColor = (status) => {
  const colors = {
    ACTIVE: "success",
    EXPIRED: "error",
    EXPIRING_SOON: "warning",
    PENDING: "info",
  };
  return colors[status] || "default";
};

// Days between two calendar days. Subtracting a UTC-parsed date-only string from a local instant
// mixed two frames of reference and left the difference up to a day out either way, so an expiry
// could read as "expiring soon" a day early or late.
const isExpiringSoon = (expiryDate, days = 30) => {
  if (!expiryDate) return false;
  const diffTime = startOfLocalDay(expiryDate) - startOfLocalDay(new Date());
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 && diffDays <= days;
};

export const CertificationsDataGrid = ({ certifications, isLoading, onEdit, onDelete, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : []),
    { field: "id", headerName: "ID", width: 80 },
    { field: "certificationName", headerName: "Certification", width: 200, flex: 1 },
    { field: "issuingOrganization", headerName: "Issuing Organization", width: 180 },
    {
      field: "issueDate",
      headerName: "Issue Date",
      width: 120,
      valueFormatter: (value) => value ? parseLocalDate(value).toLocaleDateString() : "N/A",
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      width: 140,
      renderCell: (params) => {
        const expiring = isExpiringSoon(params.value);
        const expired = Boolean(params.value) && startOfLocalDay(params.value) < startOfLocalDay(new Date());
        return (
          <MDBox display="flex" alignItems="center">
            {params.value ? parseLocalDate(params.value).toLocaleDateString() : "No Expiry"}
            {expiring && !expired && (
              <Tooltip title="Expiring Soon">
                <Warning color="warning" fontSize="small" sx={{ ml: 0.5 }} />
              </Tooltip>
            )}
          </MDBox>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        let status = params.value;
        if (!status) {
          const expiryDate = params.row.expiryDate;
          if (!expiryDate) status = "ACTIVE";
          else if (startOfLocalDay(expiryDate) < startOfLocalDay(new Date())) status = "EXPIRED";
          else if (isExpiringSoon(expiryDate)) status = "EXPIRING_SOON";
          else status = "ACTIVE";
        }
        return (
          <Chip
            label={status.replace("_", " ")}
            color={getStatusColor(status)}
            size="small"
          />
        );
      },
    },
    { field: "credentialId", headerName: "Credential ID", width: 140 },
    {
      field: "credentialUrl",
      headerName: "Verify",
      width: 80,
      renderCell: (params) =>
        params.value ? (
          <IconButton
            component={Link}
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            color="primary"
          >
            <OpenInNew />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={certifications || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id}
      />
    </MDBox>
  );
};

CertificationsDataGrid.propTypes = {
  certifications: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showActions: PropTypes.bool,
};

export default CertificationsDataGrid;

